import { useEffect, useState } from "react";
import { api, ApiError } from "../api";
import type { ScheduleSummary } from "../types";
import { describeRepeat } from "../views/scheduleText";
import type { ComponentProps } from "./index";
import { isGoneStatus, parseInteractiveResult, submitInteractiveResult } from "./interactive";
import { RepeatField } from "./RepeatField";
import { readAnswer, readProposal, scheduleDiff, type ProposalFields } from "./scheduleProposal";

type SubmitState =
	| { phase: "idle" }
	| { phase: "submitting" }
	| { phase: "submitted"; saved: boolean }
	| { phase: "failed"; status: number; message: string };

type Current = { phase: "loading" } | { phase: "loaded"; schedule: ScheduleSummary } | { phase: "failed"; message: string };

function currentError(err: unknown): string {
	if (err instanceof ApiError && err.status === 403) return "无权修改此定时任务";
	if (err instanceof ApiError && err.status === 404) return "此定时任务已删除";
	return "读取定时任务设置失败";
}

/**
 * `propose_schedule`'s card (CONTEXT.md "Proposal"): the model's draft of a schedule, or of a change to
 * one, which the person edits and saves. The components route writes the schedule before the model is
 * told, so a saved card shows the values the result carries rather than the ones proposed.
 *
 * Like Form, its arguments are data: nothing computed stands behind them.
 */
export function Schedule({ sessionId, callId, args, result }: ComponentProps): React.ReactNode {
	const proposal = readProposal(args);
	const answer = readAnswer(result ? parseInteractiveResult(result) : undefined);
	const id = proposal.ok ? proposal.id : undefined;

	// `args` streams in after mount, so the proposal is read on every render and edits are a
	// patch on top of it.
	const [edits, setEdits] = useState<Partial<ProposalFields>>({});
	const fields = proposal.ok ? { ...proposal.fields, ...edits } : null;
	const [current, setCurrent] = useState<Current | null>(null);
	const [submit, setSubmit] = useState<SubmitState>({ phase: "idle" });
	const answered = answer !== undefined;

	// A change is shown against the schedule as it stands now, not as the model last listed it.
	useEffect(() => {
		if (!id || answered) return;
		let live = true;
		setCurrent({ phase: "loading" });
		api
			.schedule(id)
			.then((d) => live && setCurrent({ phase: "loaded", schedule: d.schedule }))
			.catch((err) => live && setCurrent({ phase: "failed", message: currentError(err) }));
		return () => {
			live = false;
		};
	}, [id, answered]);

	if (!proposal.ok || !fields) return <div className="component error">{proposal.ok ? "" : proposal.error}</div>;

	const shown = answer?.kind === "saved" ? answer.fields : fields;
	const gone = answer?.kind === "gone" || (submit.phase === "failed" && isGoneStatus(submit.status));
	const locked = answered || gone || submit.phase === "submitting" || submit.phase === "submitted";
	const was = !answered && current?.phase === "loaded" ? current.schedule : undefined;
	const changed = new Set(was ? scheduleDiff(was, shown) : []);
	const cls = (field: keyof ProposalFields) => (changed.has(field) ? "form-field changed" : "form-field");

	function edit(patch: Partial<ProposalFields>) {
		setEdits((prev) => ({ ...prev, ...patch }));
	}

	async function send(saved: boolean) {
		if (saved && !shown.title.trim()) return setSubmit({ phase: "failed", status: 400, message: "标题不能为空" });
		if (saved && !shown.text.trim()) return setSubmit({ phase: "failed", status: 400, message: "消息不能为空" });
		setSubmit({ phase: "submitting" });
		const body = saved ? { saved: true, ...(id ? { id } : {}), ...shown, title: shown.title.trim() } : { saved: false };
		const outcome = await submitInteractiveResult(sessionId, callId, body);
		setSubmit(outcome.ok ? { phase: "submitted", saved } : { phase: "failed", status: outcome.status, message: outcome.message });
	}

	return (
		<div className="component schedule-card">
			<p className="component-prompt">{id ? "编辑定时任务" : "新建定时任务"}</p>
			{current?.phase === "loading" && <p className="hint">加载中…</p>}
			{current?.phase === "failed" && !answered && <p className="error">{current.message}</p>}

			<div className="form-fields">
				<label className={cls("title")}>
					标题
					<input type="text" value={shown.title} disabled={locked} onChange={(e) => edit({ title: e.target.value })} />
					{was && changed.has("title") && <span className="form-field-hint">原值：{was.title}</span>}
				</label>
				<div className={cls("repeat")}>
					重复
					<RepeatField repeat={shown.repeat} disabled={locked} onChange={(repeat) => edit({ repeat })} />
					{was && changed.has("repeat") && <span className="form-field-hint">原值：{describeRepeat(was.repeat)}</span>}
				</div>
				<label className={cls("text")}>
					消息
					<textarea rows={6} value={shown.text} disabled={locked} onChange={(e) => edit({ text: e.target.value })} />
				</label>
				{was && changed.has("text") && (
					<details className="form-field-hint">
						<summary>原消息</summary>
						<pre className="schedule-old-text">{was.text}</pre>
					</details>
				)}
				<label className={`${cls("notify")} inline`}>
					<input type="checkbox" checked={shown.notify} disabled={locked} onChange={(e) => edit({ notify: e.target.checked })} />
					推送成功结果
				</label>
			</div>

			{answer?.kind === "saved" && <p className="hint">{answer.paused ? "已保存 · 已暂停" : `已保存 · 下次 ${answer.nextRun ?? "—"}`}</p>}
			{answer?.kind === "declined" && <p className="hint">未保存</p>}
			{gone && <p className="hint">卡片已失效，未保存</p>}
			{answer?.kind === "unreadable" && <p className="error">已处理，无法读取结果</p>}
			{!answered && submit.phase === "failed" && !gone && <p className="error">{submit.message}</p>}

			{!answered && !gone && (
				<div className="row">
					<button onClick={() => void send(true)} disabled={locked || current?.phase === "loading" || current?.phase === "failed"}>
						{submit.phase === "submitting" ? "保存中…" : submit.phase === "submitted" ? (submit.saved ? "已保存" : "已跳过") : "保存"}
					</button>
					<button className="ghost" onClick={() => void send(false)} disabled={locked}>
						不保存
					</button>
				</div>
			)}
		</div>
	);
}
